'use client'

import { memo } from 'react'
import { cn } from '@/lib/utils'

type NinDeviceMockProps = {
  kind: 'laptop' | 'phone'
  className?: string
}

/**
 * Half-visible device chrome for product cards: clearer silhouette,
 * still soft so copy stays primary.
 */
export const NinDeviceMock = memo(function NinDeviceMock({
  kind,
  className,
}: NinDeviceMockProps) {
  if (kind === 'phone') {
    return (
      <div
        className={cn(
          'pointer-events-none absolute -right-5 top-[52%] w-[6.75rem] -translate-y-1/2 rotate-[8deg] opacity-50 transition duration-300 group-hover:translate-x-[-2px] group-hover:opacity-70 sm:-right-3 sm:w-[7.5rem] md:opacity-55 md:group-hover:opacity-75',
          className,
        )}
        aria-hidden
      >
        {/* Phone shell */}
        <div
          className="relative aspect-[9/19] w-full rounded-[1.25rem] border-[2.5px] border-[color:var(--ninxl-8bit-border)] bg-[color:var(--ninxl-theme-bg-frame)]"
          style={{ boxShadow: '4px 4px 0 0 var(--ninxl-8bit-dim)' }}
        >
          {/* Side buttons */}
          <div className="absolute -left-[3px] top-10 h-3 w-[3px] rounded-l-sm bg-[color:var(--ninxl-8bit-border)]" />
          <div className="absolute -left-[3px] top-16 h-5 w-[3px] rounded-l-sm bg-[color:var(--ninxl-8bit-border)]" />
          <div className="absolute -right-[3px] top-14 h-6 w-[3px] rounded-r-sm bg-[color:var(--ninxl-8bit-border)]" />

          {/* Dynamic island */}
          <div className="absolute left-1/2 top-2 z-10 h-2.5 w-10 -translate-x-1/2 rounded-full bg-[color:var(--ninxl-8bit-border)]" />

          {/* Screen */}
          <div className="absolute inset-[5px] overflow-hidden rounded-[1rem] bg-[color:var(--ninxl-theme-atmosphere)]">
            {/* Status bar */}
            <div className="flex items-center justify-between px-2 pt-5">
              <div className="h-1 w-4 rounded-sm bg-[color:var(--ninxl-8bit-border)]/40" />
              <div className="flex gap-0.5">
                <div className="h-1 w-1 rounded-full bg-[color:var(--ninxl-brand-teal)]/60" />
                <div className="h-1 w-2 rounded-sm bg-[color:var(--ninxl-8bit-border)]/40" />
              </div>
            </div>

            {/* App header */}
            <div className="mt-2 flex items-center gap-1.5 px-2">
              <div className="h-4 w-4 rounded-md bg-[color:var(--ninxl-brand-teal)]/50" />
              <div className="h-1.5 w-10 rounded-sm bg-[color:var(--ninxl-8bit-border)]/35" />
            </div>

            {/* Chat bubbles */}
            <div className="mt-3 flex flex-col gap-1.5 px-2">
              <div className="h-5 w-[70%] rounded-md rounded-bl-sm bg-[color:var(--ninxl-brand-teal)]/25" />
              <div className="ml-auto h-5 w-[55%] rounded-md rounded-br-sm bg-[color:var(--ninxl-8bit-border)]/20" />
              <div className="h-5 w-[60%] rounded-md rounded-bl-sm bg-[color:var(--ninxl-brand-teal)]/20" />
              <div className="ml-auto h-4 w-[45%] rounded-md rounded-br-sm bg-[color:var(--ninxl-8bit-border)]/15" />
            </div>

            {/* Composer */}
            <div className="absolute inset-x-1.5 bottom-2 flex items-center gap-1 rounded-full border border-[color:var(--ninxl-8bit-border)]/30 bg-[color:var(--ninxl-theme-bg-frame)] px-1.5 py-1">
              <div className="h-1.5 flex-1 rounded-sm bg-[color:var(--ninxl-8bit-border)]/20" />
              <div className="h-3 w-3 rounded-full bg-[color:var(--ninxl-brand-teal)]/55" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'pointer-events-none absolute -right-12 top-[48%] w-[11.5rem] -translate-y-1/2 rotate-[-6deg] opacity-50 transition duration-300 group-hover:translate-x-[-3px] group-hover:opacity-70 sm:-right-8 sm:w-[13rem] md:opacity-55 md:group-hover:opacity-75',
        className,
      )}
      aria-hidden
    >
      {/* Screen lid */}
      <div
        className="relative aspect-[16/10] w-full rounded-t-lg border-[2.5px] border-b-[3px] border-[color:var(--ninxl-8bit-border)] bg-[color:var(--ninxl-theme-bg-frame)]"
        style={{ boxShadow: '4px 4px 0 0 var(--ninxl-8bit-dim)' }}
      >
        {/* Screen bezel */}
        <div className="absolute inset-[6px] overflow-hidden rounded-sm bg-[color:var(--ninxl-theme-atmosphere)]">
          {/* Camera */}
          <div className="absolute left-1/2 top-1 z-10 h-1 w-1 -translate-x-1/2 rounded-full bg-[color:var(--ninxl-8bit-border)]/50" />

          {/* Title bar */}
          <div className="flex items-center gap-1 border-b border-[color:var(--ninxl-8bit-border)]/20 px-2 py-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-[#e85d4c]/70" />
            <div className="h-1.5 w-1.5 rounded-full bg-[#f0c14a]/70" />
            <div className="h-1.5 w-1.5 rounded-full bg-[#3dbe6a]/70" />
            <div className="ml-2 h-1.5 flex-1 rounded-sm bg-[color:var(--ninxl-8bit-border)]/15" />
          </div>

          {/* App chrome */}
          <div className="flex h-[calc(100%-1.25rem)]">
            {/* Sidebar */}
            <div className="flex w-5 flex-col gap-1 border-r border-[color:var(--ninxl-8bit-border)]/15 p-1">
              <div className="h-2 w-full rounded-sm bg-[color:var(--ninxl-brand-teal)]/40" />
              <div className="h-2 w-full rounded-sm bg-[color:var(--ninxl-8bit-border)]/20" />
              <div className="h-2 w-full rounded-sm bg-[color:var(--ninxl-8bit-border)]/15" />
            </div>
            {/* Main pane */}
            <div className="flex flex-1 flex-col gap-1.5 p-2">
              <div className="h-2 w-2/3 rounded-sm bg-[color:var(--ninxl-brand-teal)]/35" />
              <div className="h-1.5 w-full rounded-sm bg-[color:var(--ninxl-8bit-border)]/18" />
              <div className="h-1.5 w-4/5 rounded-sm bg-[color:var(--ninxl-8bit-border)]/14" />
              <div className="mt-1 grid flex-1 grid-cols-2 gap-1">
                <div className="rounded-sm bg-[color:var(--ninxl-brand-teal)]/12" />
                <div className="rounded-sm bg-[color:var(--ninxl-8bit-border)]/10" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hinge + deck */}
      <div className="relative -mt-px h-1 w-full bg-[color:var(--ninxl-8bit-border)]/40" />
      <div
        className="relative h-3.5 w-[114%] -translate-x-[6%] rounded-b-lg border-[2.5px] border-t-0 border-[color:var(--ninxl-8bit-border)] bg-[color:var(--ninxl-theme-bg-frame)]"
        style={{ boxShadow: '4px 4px 0 0 var(--ninxl-8bit-dim)' }}
      >
        {/* Trackpad */}
        <div className="absolute left-1/2 top-1 h-2 w-12 -translate-x-1/2 rounded-sm border border-[color:var(--ninxl-8bit-border)]/30 bg-[color:var(--ninxl-theme-atmosphere)]" />
      </div>
    </div>
  )
})
